import { z } from 'zod';
/**
 * Tipos que aparecem em mais de um dominio. Ficam aqui para nao serem redefinidos
 * com pequenas diferencas em cada arquivo — foi assim que `clients` acabou com tres
 * esquemas materialmente diferentes em tres bancos (docs/NYROH.md, secao 3).
 */
export const idSchema = z.uuid();
export const slugSchema = z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug deve ser minusculo, sem acento, separado por hifen');
/**
 * O limite de tamanho do slug, em caracteres.
 *
 * Menor que o `max(120)` do schema de proposito: quem GERA para com folga, e o
 * schema recusa so o que passou por outro caminho. Um gerador que para exatamente
 * no limite do validador nao tem margem para o proximo sufixo (`-2`) sem quebrar.
 */
export const LIMITE_SLUG = 100;
/**
 * ============================================================================
 * A NORMALIZACAO DE SLUG — uma implementacao, ao lado do schema que a valida
 * ============================================================================
 *
 * Ela vive AQUI, e nao em cada app, porque ja existiram cinco versoes
 * ligeiramente diferentes espalhadas pelo repositorio — e slug divergente entre
 * quem grava e quem le e como um site publicado "some".
 *
 * O caso concreto que trouxe esta funcao para o contrato: `zipHandler.ts`
 * montava o slug sem NENHUM limite de tamanho. Um projeto com nome comprido
 * gerava um slug de mais de 120 caracteres, `slugSchema` recusava, e como o
 * cliente HTTP valida o corpo inteiro antes de enviar, UM projeto ruim derrubava
 * a importacao dos outros 134 — com uma mensagem que era o despejo cru do zod.
 *
 * Ficar do lado do `slugSchema` e o ponto: quem mexer no limite ve as duas
 * coisas na mesma tela.
 */
export function paraSlug(texto) {
    return texto
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, LIMITE_SLUG)
        // O `slice` pode terminar em hifen, e ai o slug nao casa mais com o regex do
        // schema. Cortar depois de truncar, nao antes.
        .replace(/-+$/, '');
}
/**
 * Slugs unicos dentro de UM lote, preservando a ordem.
 *
 * Truncar em `LIMITE_SLUG` faz dois nomes compridos e parecidos colidirem. A
 * importacao usa `ON CONFLICT DO NOTHING`, entao a colisao nao daria erro — ela
 * sumiria com o segundo projeto, contado como "ignorado porque ja existe". Um
 * projeto perdido em silencio e pior que um slug feio.
 */
export function slugsUnicos(textos) {
    const vistos = new Map();
    return textos.map((t) => {
        const base = paraSlug(t);
        const quantos = vistos.get(base) ?? 0;
        vistos.set(base, quantos + 1);
        if (quantos === 0)
            return base;
        const sufixo = `-${quantos + 1}`;
        return base.slice(0, LIMITE_SLUG - sufixo.length).replace(/-+$/, '') + sufixo;
    });
}
/**
 * Paginacao por cursor, nunca OFFSET.
 *
 * O painel hoje pede 100.000 registros em blocos de 100 com `.range()`, o que no
 * Postgres significa reler e descartar todas as linhas anteriores a cada bloco —
 * mil requisicoes por carregamento, cada uma mais lenta que a anterior. O cursor
 * usa o indice `(created_at DESC, id DESC)` e o custo por pagina fica constante.
 */
export const paginacaoSchema = z.object({
    /** Opaco. Vem do campo `proximoCursor` da resposta anterior. */
    cursor: z.string().optional(),
    limite: z.coerce.number().int().min(1).max(200).default(50),
});
export function paginadoSchema(item) {
    return z.object({
        itens: z.array(item),
        proximoCursor: z.string().nullable(),
        /** Contagem total so quando barata. `null` significa "nao contei", nao "zero". */
        total: z.number().int().nullable().default(null),
    });
}
/** Formato unico de erro da API. O cliente HTTP transforma isto em `NyrohApiError`. */
export const erroApiSchema = z.object({
    erro: z.object({
        codigo: z.string(),
        mensagem: z.string(),
        detalhes: z.unknown().optional(),
    }),
});
export const vazioSchema = z.object({}).loose();
/** ISO 8601. O banco da fabrica guardava `created_at` como epoch em bigint e o da
 *  plataforma como timestamptz — a mesma coluna com dois tipos. Aqui e sempre string ISO. */
export const dataHoraSchema = z.iso.datetime({ offset: true });
//# sourceMappingURL=comum.js.map